import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { approve, listQueue, reject, type QueueEntry } from '../api/moderation';
import { useAuth } from '../auth/useAuth';
import { AppShell } from '../components/layout/AppShell';
import { ReportQueue } from '../components/moderation/ReportQueue';
import { Alert, Badge, Button, Card, EmptyState, ErrorState, LoadingState } from '../components/ui';
import './ModerationPage.css';

type State = 'loading' | 'ready' | 'error';

function when(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * `/moderation` — what is waiting to be read.
 *
 * Oldest first, because newest-first would bury the submission somebody has
 * been waiting longest on. Approving is one click; refusing asks for the reason
 * first, because a refusal without one is not a decision the author can act on
 * — and the backend refuses it anyway.
 */
export function ModerationPage() {
  const { account } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'reports' ? 'reports' : 'submissions';
  const [state, setState] = useState<State>('loading');
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [working, setWorking] = useState('');

  const load = useCallback(async () => {
    try {
      const page = await listQueue();
      setEntries(page.results);
      setState('ready');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (id: string, action: () => Promise<unknown>) => {
    setWorking(id);
    setError(null);
    try {
      await action();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking('');
    }
  };

  const refuse = (entry: QueueEntry) => {
    const reason = window.prompt(
      `Why is “${entry.title}” not being published?\n\nThe author sees this, and it is the only part of a refusal they can act on.`,
    );
    if (reason === null || !reason.trim()) {
      return;
    }
    void run(entry.id, () => reject(entry.id, reason.trim()));
  };

  if (account && account.role === 'MEMBER') {
    return (
      // With the page's own heading, like every other state of it. Phase 29's
      // audit ran as a member for the first time and found this: the refusal
      // rendered a page with no `h1` at all, so a screen reader arriving here
      // was told nothing about where "here" was.
      <AppShell header={<h1>Review</h1>}>
        <EmptyState
          title="This is for moderators"
          description="Ask an administrator if you think you should be one."
        />
      </AppShell>
    );
  }

  const tabs = (
    <nav className="sc-queue__tabs" aria-label="Moderation queues">
      <button
        type="button"
        className={tab === 'submissions' ? 'is-active' : ''}
        aria-current={tab === 'submissions' ? 'page' : undefined}
        onClick={() => setParams({})}
      >
        Waiting for review
      </button>
      <button
        type="button"
        className={tab === 'reports' ? 'is-active' : ''}
        aria-current={tab === 'reports' ? 'page' : undefined}
        onClick={() => setParams({ tab: 'reports' })}
      >
        Reported
      </button>
    </nav>
  );

  if (tab === 'reports') {
    return (
      <AppShell
        header={
          <div className="sc-page-header">
            <div>
              <h1>Reported</h1>
              <p className="sc-queue__muted">
                What readers have told us about. The first button acts on what the report names and
                is written down as yours; the last two only record that you looked. No number of
                reports moves anything by itself.
              </p>
            </div>
          </div>
        }
      >
        {tabs}
        <ReportQueue />
      </AppShell>
    );
  }

  if (state === 'loading') {
    return (
      <AppShell>
        <LoadingState label="Reading the queue…" />
      </AppShell>
    );
  }

  if (state === 'error') {
    return (
      <AppShell>
        <ErrorState
          title="We could not read the queue"
          description={error?.message}
          onRetry={() => void load()}
        />
      </AppShell>
    );
  }

  return (
    <AppShell
      header={
        <div className="sc-page-header">
          <div>
            <h1>Waiting for review</h1>
            <p className="sc-queue__muted">
              Oldest first. Nothing here is public until somebody says so.
            </p>
          </div>
        </div>
      }
    >
      {tabs}

      {error && (
        <Alert tone="warning" title="That did not work">
          {error.message}
        </Alert>
      )}

      {entries.length === 0 ? (
        <EmptyState
          title="Nothing is waiting"
          description="When somebody hands a story over for review, it appears here."
        />
      ) : (
        <ul className="sc-queue">
          {entries.map((entry) => (
            <li key={entry.id}>
              <Card>
                <div className="sc-queue__head">
                  <div>
                    <h2 className="sc-queue__title">
                      <Link to={`/stories/${entry.post.id}?version=${entry.number}`}>
                        {entry.title || 'Untitled'}
                      </Link>
                    </h2>
                    {entry.subtitle && <p className="sc-queue__muted">{entry.subtitle}</p>}
                    <p className="sc-queue__muted">
                      {/* A name that is only the handle again is not two facts. */}
                      {entry.author.display_name === entry.author.handle
                        ? `@${entry.author.handle}`
                        : `${entry.author.display_name} · @${entry.author.handle}`}{' '}
                      · version {entry.number} · submitted {when(entry.submitted_at)}
                    </p>
                  </div>
                  {entry.history_count > 0 && (
                    <Badge tone="accent">
                      {entry.history_count === 1
                        ? 'Looked at once before'
                        : `Looked at ${entry.history_count} times before`}
                    </Badge>
                  )}
                </div>

                <div className="sc-queue__actions">
                  <Button
                    size="sm"
                    isLoading={working === entry.id}
                    onClick={() => void run(entry.id, () => approve(entry.id))}
                  >
                    Approve and publish
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    isLoading={working === entry.id}
                    onClick={() => refuse(entry)}
                  >
                    Not yet…
                  </Button>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
}
